// Facility landing pages (COM-773/774) — /kitchens/<slug>, the destination
// for the per-facility Google Ads campaigns. Content comes from
// data/facilityPages.ts; the single ApplyLink CTA is the conversion path.
import React, { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { afterPageView, fireFacilityView } from '../lib/attribution';
import { posthog } from '../lib/posthog';
import ApplyLink from '../components/ApplyLink';
import { FACILITIES } from '../data/facilityPages';
import './FacilityPage.css';

const FacilityPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const facility = slug ? FACILITIES[slug] : undefined;

  // A dead facility slug is invisible TWICE unless it says so here, and that is
  // how `/kitchens/richmond` sat in drafted ad copy recorded as "verified 200".
  //
  //  1. The SPA answers HTTP 200 for every path, so a status-code check passes
  //     any typo'd or retired slug forever — `/kitchens/definitely-not-real`
  //     returns 200 and only the browser redirects.
  //  2. `page_not_found` is reported by pages/NotFound.tsx, and an unknown
  //     /kitchens/:slug NEVER REACHES IT — this component matches the route
  //     first and redirects. So the one signal that does catch dead links had a
  //     hole exactly where the ad-targeted URLs live.
  //
  // Report before redirecting. The redirect itself is deliberate and stays: a
  // visitor who followed a stale ad should land somewhere useful, not on a 404.
  // Same event name and shape as NotFound so both land in one PostHog series;
  // `facility_slug` is what tells them apart when auditing ads and sitelinks.
  useEffect(() => {
    if (facility || !slug) return;
    try {
      if (posthog.__loaded) {
        posthog.capture('page_not_found', {
          path: window.location.pathname,
          referrer: document.referrer || null,
          facility_slug: slug,
        });
      }
    } catch { /* degrade silently — never block the redirect on telemetry */ }
  }, [facility, slug]);

  // GA4 micro-funnel: which facility pages ad clicks actually land on.
  useEffect(() => {
    if (!facility) return;
    window.scrollTo(0, 0);
    // Deferred (COM-1029): this mount effect runs BEFORE PageViewTracker's, so
    // firing straight away sent GA4 a view_facility for a page it had no
    // page_view for. afterPageView releases it the moment the page_view is out
    // — and immediately on a direct landing, where config already sent one.
    afterPageView(() => {
      window.gtag?.('event', 'view_facility', { facility: facility.slug });
      // The same moment, mirrored to PostHog (CMSY-30) so replays and heatmaps
      // can be joined to the facility — and to the v2 id the Apply CTA carries.
      fireFacilityView(facility.slug, facility.v2FacilityId);
    });
  }, [facility]);

  // FoodEstablishment structured data — SEO.tsx only handles meta tags.
  useEffect(() => {
    if (!facility) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FoodEstablishment',
      name: facility.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: facility.address,
        addressRegion: 'BC',
        addressCountry: 'CA',
      },
      telephone: '+1-778-558-7272',
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [facility]);

  if (!facility) {
    return <Navigate to="/our-kitchens" replace />;
  }

  return (
    <div className="facility-page">
      <SEO
        title={facility.seoTitle ?? `${facility.name} | Commissary Connect`}
        description={facility.seoDescription}
      />

      <div className="facility-hero">
        <img className="facility-hero-image" src={facility.image} alt={facility.name} />
        <div className="facility-hero-overlay">
          <div className="container">
            <span className={`status-badge ${facility.status}`}>
              {facility.status === 'operating' ? 'Available Now' : 'Coming Soon'}
            </span>
            <h1>{facility.headline}</h1>
            <p className="facility-subline">{facility.subline}</p>
          </div>
        </div>
      </div>

      <div className="facility-body">
        <div className="container">
          <div className="facility-title-row">
            <div>
              <h2>{facility.name}</h2>
              <p className="facility-address">{facility.address}</p>
              {(facility.size || facility.capacity) && (
                <p className="facility-size-capacity">
                  {[facility.size, facility.capacity].filter(Boolean).join(' • ')}
                </p>
              )}
            </div>
            <div className="facility-cta-block">
              <ApplyLink className="facility-apply-btn" label={facility.ctaLabel} facilities={facility.v2FacilityId}>
                {facility.ctaText}
              </ApplyLink>
              <p className="facility-phone">
                Questions?{' '}
                <a href="tel:7785587272" data-phone-label={`facility_${facility.slug}`}>
                  778.558.7272
                </a>
              </p>
            </div>
          </div>

          {facility.features.length > 0 && (
            <div className="facility-section">
              <h3>Highlights</h3>
              <div className="facility-tags">
                {facility.features.map((feature) => (
                  <span key={feature} className="facility-tag accent">{feature}</span>
                ))}
              </div>
            </div>
          )}

          {facility.idealFor.length > 0 && (
            <div className="facility-section">
              <h3>Ideal For</h3>
              <div className="facility-tags">
                {facility.idealFor.map((item) => (
                  <span key={item} className="facility-tag">{item}</span>
                ))}
              </div>
            </div>
          )}

          {facility.kitchenEquipment.length > 0 && (
            <div className="facility-section">
              <h3>Kitchen Equipment</h3>
              <div className="facility-icon-grid">
                {facility.kitchenEquipment.map((item) => (
                  <div key={item.name} className="facility-icon-item">
                    <img src={item.icon} alt="" />
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {facility.includedServices.length > 0 && (
            <div className="facility-section">
              <h3>Included Services</h3>
              <div className="facility-icon-grid">
                {facility.includedServices.map((item) => (
                  <div key={item.name} className="facility-icon-item">
                    <img src={item.icon} alt="" />
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FacilityPage;
